import { useEffect, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { DateTime } from "luxon"
import { useField, useFormikContext } from "formik"
import { Box, Divider, FormHelperText, Grid, Switch, Tooltip, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleExclamation } from "@fortawesome/pro-regular-svg-icons"
import {
    FormikNumberField,
    FormikSwitch,
    FormikTextArea,
    FormikTextField,
} from "../../../../../shared/components/forms/Formik"
import { MAX_COUNT_VALUE } from "../../../../../constants/inputValidationDefaults"
import { FormikImageUpload } from "../../../../../shared/components/Formik/FormikImageUpload"
import { StandardMainPanel } from "../../../../../shared/components/forms/StandardMainPanel"
import { StandardSidePanel } from "../../../../../shared/components/forms/StandardSidePanel"
import { StandardForm } from "../../../../../shared/components/forms/StandardForm"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { AvailabilityDetailsField } from "./AvailabilityDetailsField"
import { FormikProductGroupField } from "./FormikProductGroupField"
import { CreateUpdateProductFormValues, OfferDetailsType } from "../productsTypes"
import { FormikAllergensField } from "./FormikAllergensField"
import { DeadlineField, dayOptions } from "./DeadlineField"
import { SalesRuleRow } from "./SalesRuleRow"
import { ProductPriceField } from "./ProductPriceField"
import { OfferDetails } from "./offerDetails/OfferDetails"
// import { ItemLimitsField } from "./ItemLimitsField"
import { FormikPriceListField } from "./FormikPriceListsField"
import { StockField } from "./stockManagement/StockField"
import { InfoBox } from "../../shops/components/formElements/InfoBox"
import { useProductGroupAvailabilitySync } from "../hooks/useProductGroupAvailabilitySync"

const useStyles = makeStyles()((theme) => ({
    priceDisabledIcon: {
        color: theme.palette.tailwind.amber[500],
    },
    hidePriceSwitch: {
        "& .MuiFormControl-root": {
            width: "10rem",
        },
    },
    generalRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    },
    onSaleInput: {
        width: "5rem",
        "& .MuiInputBase-root": {
            height: "2.5rem",
        },
        "& .MuiInputBase-input": {
            textAlign: "right",
        },
    },
}))

type CreateUpdateProductFormContentProps = Readonly<{
    isEditMode?: boolean
}>

function OnSalePerDayField() {
    const { classes } = useStyles()
    const { submitCount } = useFormikContext()
    const [, meta] = useField("itemsForSalePerDay")
    const showError = (meta.touched || submitCount > 0) && Boolean(meta.error)

    return (
        <Box>
            <Box className={classes.generalRow}>
                <LocalizedStrict id="product-stock-on-sale-per-day-label">
                    <Typography variant="body1">On sale per day:</Typography>
                </LocalizedStrict>
                <FormikNumberField
                    name="itemsForSalePerDay"
                    integerOnly
                    maxLength={4}
                    min={0}
                    max={MAX_COUNT_VALUE}
                    className={classes.onSaleInput}
                    size="small"
                    FormHelperTextProps={{ style: { display: "none" } }}
                />
            </Box>
            {showError && (
                <FormHelperText error sx={{ mt: 0 }}>
                    {meta.error}
                </FormHelperText>
            )}
        </Box>
    )
}

export function CreateUpdateProductFormContent({ isEditMode }: CreateUpdateProductFormContentProps) {
    const { classes } = useStyles()
    const { values, setFieldValue } = useFormikContext<CreateUpdateProductFormValues>()
    useProductGroupAvailabilitySync()
    const defaultTime = DateTime.fromISO("12:00")
    const priceListId = values.priceList?.id
    const previousPriceListId = useRef(priceListId)
    const [searchParams] = useSearchParams()

    const [checkedAvailability, setCheckedAvailability] = useState(
        !!(
            values?.availabilityDetails?.dateInterval?.from ||
            values?.availabilityDetails?.dateInterval?.to ||
            values?.availabilityDetails?.weekDays?.length ||
            values?.availabilityDetails?.timeInterval?.start ||
            values?.availabilityDetails?.timeInterval?.end
        ),
    )
    const [checkedDeadline, setCheckedDeadline] = useState(!!values?.deadline)
    const [checkedItemLimits, setCheckedItemLimits] = useState(!!values?.itemLimits)
    const [checkedOffer, setCheckedOffer] = useState(!!values?.offerDetails)
    const [checkedStock, setCheckedStock] = useState(values?.stock != null)
    const [offerDialogState, setOfferDialogState] = useState<boolean>(
        searchParams.get("openOffer") === "true" && !!values?.offerDetails,
    )
    const [cachedAvailability, setCachedAvailability] = useState({
        dateInterval: values?.availabilityDetails?.dateInterval,
        weekDays: values?.availabilityDetails?.weekDays ?? [],
        timeInterval: values?.availabilityDetails?.timeInterval,
    })
    const [cachedDeadline, setCachedDeadline] = useState(values?.deadline)
    const [cachedItemLimits, setCachedItemLimits] = useState(values?.itemLimits)
    const [cachedOfferDetails, setCachedOfferDetails] = useState(values?.offerDetails)
    const [cachedStock, setCachedStock] = useState(values?.stock)
    const handleChangeAvailability = (checked: boolean) => {
        setCheckedAvailability(checked)
        if (checked) {
            setFieldValue(
                "availabilityDetails.dateInterval",
                cachedAvailability.dateInterval ?? { from: null, to: null },
            )
            setFieldValue("availabilityDetails.weekDays", cachedAvailability.weekDays)
            setFieldValue(
                "availabilityDetails.timeInterval",
                cachedAvailability.timeInterval ?? { start: null, end: null },
            )
        } else {
            setCachedAvailability({
                dateInterval: values?.availabilityDetails?.dateInterval,
                weekDays: values?.availabilityDetails?.weekDays ?? [],
                timeInterval: values?.availabilityDetails?.timeInterval,
            })
            setFieldValue("availabilityDetails.dateInterval", undefined)
            setFieldValue("availabilityDetails.weekDays", [])
            setFieldValue("availabilityDetails.timeInterval", undefined)
        }
    }
    const handleChangeDeadline = (checked: boolean) => {
        setCheckedDeadline(checked)
        if (checked) {
            const restored = cachedDeadline ?? {
                purchases: { daysBefore: dayOptions[0].value, timeOfDay: defaultTime },
                // hide cancellation for now
                // cancellation: { daysBefore: dayOptions[0].value, timeOfDay: defaultTime },
            }
            setFieldValue("deadline", restored)
        } else {
            setCachedDeadline(values?.deadline)
            setFieldValue("deadline", null)
        }
    }
    const handleChangeItemLimits = (checked: boolean) => {
        setCheckedItemLimits(checked)
        if (checked) {
            const restored = cachedItemLimits ?? {
                maxDailyItemsPerPerson: "",
                minItemsPerOrder: "",
            }
            setFieldValue("itemLimits", restored)
        } else {
            setCachedItemLimits(values?.itemLimits)
            setFieldValue("itemLimits", null)
        }
    }
    const offerSavedRef = useRef(!!values?.offerDetails)
    const handleChangeOffer = (checked: boolean) => {
        setCheckedOffer(checked)
        if (checked) {
            if (cachedOfferDetails) {
                setFieldValue("offerDetails", cachedOfferDetails)
            } else {
                offerSavedRef.current = false
                setOfferDialogState(true)
            }
        } else {
            setCachedOfferDetails(values?.offerDetails)
            setFieldValue("offerDetails", undefined)
        }
    }
    const handleOfferSave = (data: OfferDetailsType) => {
        offerSavedRef.current = true
        setFieldValue("offerDetails", data)
        setCheckedOffer(true)
    }
    const handleOfferDialogClose = () => {
        setOfferDialogState(false)
        if (!offerSavedRef.current) {
            setCheckedOffer(false)
        }
    }
    const handleChangeStock = (checked: boolean) => {
        setCheckedStock(checked)
        if (checked) {
            const restored = cachedStock ?? {
                total: undefined,
                inventoryIncomplete: false,
            }
            setFieldValue("stock", restored)
        } else {
            setCachedStock(values?.stock)
            setFieldValue("stock", null)
        }
    }

    // Compare with the previous price list instead of skipping the first run: React.StrictMode mounts effects twice,
    // so a "first render" flag let the second run clear the product group of the product being edited.
    useEffect(() => {
        if (previousPriceListId.current === priceListId) return
        previousPriceListId.current = priceListId
        setFieldValue("productGroup", null)
    }, [priceListId])

    useEffect(() => {
        if (values.variablePrice) {
            setFieldValue("price", "")
            setFieldValue("isPriceHidden", false)
        }
    }, [values.variablePrice])

    return (
        <>
            <StandardForm>
                <StandardMainPanel>
                    {/* <FormikPriceListsField name="priceLists" /> */}
                    <FormikPriceListField name="priceList" />

                    <FormikProductGroupField name="productGroup" priceListId={priceListId} />

                    <LocalizedStrict id="product-create-name-field-label" attrs={{ label: true }}>
                        <FormikTextField name="name" label="Product name" fullWidth />
                    </LocalizedStrict>

                    <Grid container item spacing={1} direction="row" alignItems="flex-end">
                        <Grid item sm={5}>
                            <ProductPriceField />
                        </Grid>
                        {values.variablePrice && (
                            <Grid item display="flex" alignItems="center" height="3.5rem">
                                <LocalizedStrict id="product-create-price-disabled-tooltip" attrs={{ title: true }}>
                                    <Tooltip
                                        arrow
                                        placement="top"
                                        title="Price is disabled because variable price is active"
                                    >
                                        <FontAwesomeIcon
                                            icon={faCircleExclamation}
                                            size="lg"
                                            className={classes.priceDisabledIcon}
                                        />
                                    </Tooltip>
                                </LocalizedStrict>
                            </Grid>
                        )}
                        <Grid
                            item
                            sm
                            flexGrow={1}
                            display="flex"
                            alignItems="center"
                            justifyContent="flex-end"
                            height="3.5rem"
                            className={classes.hidePriceSwitch}
                        >
                            <LocalizedStrict id="product-create-payment-price-switch">
                                <FormikSwitch<CreateUpdateProductFormValues>
                                    name="isPriceHidden"
                                    disabled={values.variablePrice}
                                >
                                    Hide price:
                                </FormikSwitch>
                            </LocalizedStrict>
                        </Grid>
                    </Grid>
                </StandardMainPanel>
                <LocalizedStrict id="product-create-panel-description" attrs={{ title: true }}>
                    <StandardMainPanel title="Description">
                        <LocalizedStrict
                            id="product-create-product-description-marketing-tag-field"
                            attrs={{ label: true, helperText: true }}
                        >
                            <FormikTextField
                                name="productDescription.marketingTag"
                                label="Marketing tag"
                                helperText="Examples: 'Offer', 'CO2', 'Hot dish', 'Vegetarian'"
                                fullWidth
                            />
                        </LocalizedStrict>
                        <FormikAllergensField name="productDescription.allergens" />
                        <LocalizedStrict id="product-create-description-field-label" attrs={{ label: true }}>
                            <FormikTextArea name="description" label="Description" fullWidth />
                        </LocalizedStrict>
                        <LocalizedStrict id="product-create-image-field-label" attrs={{ label: true }}>
                            <FormikImageUpload label="Image" name="productPhoto" typeOfEntity="product" />
                        </LocalizedStrict>
                    </StandardMainPanel>
                </LocalizedStrict>
                <LocalizedStrict id="product-create-general-panel" attrs={{ title: true }}>
                    <StandardSidePanel title="General" variant="collapsible">
                        <LocalizedStrict id="product-create-active-switch">
                            <FormikSwitch name="isActive">Active</FormikSwitch>
                        </LocalizedStrict>
                        <LocalizedStrict id="product-create-admin-only-switch">
                            <FormikSwitch<CreateUpdateProductFormValues> name="isAdminOnly">Staff only</FormikSwitch>
                        </LocalizedStrict>
                        <Divider />
                        <OnSalePerDayField />
                        <LocalizedStrict id="product-stock-on-sale-per-day-info" attrs={{ description: true }}>
                            <InfoBox description="Using this you can limit the number of items on sale each day." />
                        </LocalizedStrict>
                        <Divider />
                        <Box className={classes.generalRow}>
                            <LocalizedStrict id="product-create-stock-switch-label">
                                <Typography variant="body1">Stock management:</Typography>
                            </LocalizedStrict>
                            <Switch checked={checkedStock} onChange={(_, checked) => handleChangeStock(checked)} />
                        </Box>
                        {checkedStock && <StockField />}
                    </StandardSidePanel>
                </LocalizedStrict>
                <LocalizedStrict id="product-create-pos-panel" attrs={{ title: true }}>
                    <StandardSidePanel title="POS" variant="collapsible">
                        <LocalizedStrict id="product-create-variable-price-switch">
                            <FormikSwitch<CreateUpdateProductFormValues> name="variablePrice">
                                Activate variable price
                            </FormikSwitch>
                        </LocalizedStrict>
                        <LocalizedStrict id="product-create-require-staff-pin-switch">
                            <FormikSwitch<CreateUpdateProductFormValues> name="isLocked">
                                Require staff PIN
                            </FormikSwitch>
                        </LocalizedStrict>
                    </StandardSidePanel>
                </LocalizedStrict>
                <LocalizedStrict id="product-create-sales-rules-panel" attrs={{ title: true }}>
                    <StandardSidePanel title="Sales rules" variant="group">
                        <LocalizedStrict id="product-create-offer-panel" attrs={{ title: true, description: true }}>
                            <SalesRuleRow
                                title="Offer"
                                description="Temporarily reduced price for a period"
                                checked={checkedOffer}
                                onCheckChange={handleChangeOffer}
                            >
                                <OfferDetails
                                    dialogState={offerDialogState}
                                    setDialogState={setOfferDialogState}
                                    setCheckedOffer={setCheckedOffer}
                                    onDialogClose={handleOfferDialogClose}
                                    onSaveOffer={handleOfferSave}
                                />
                            </SalesRuleRow>
                        </LocalizedStrict>
                        <LocalizedStrict
                            id="product-create-availability-panel"
                            attrs={{ title: true, description: true }}
                        >
                            <SalesRuleRow
                                title="Availability"
                                description="Limit to specific days and times"
                                checked={checkedAvailability}
                                onCheckChange={handleChangeAvailability}
                            >
                                <AvailabilityDetailsField />
                            </SalesRuleRow>
                        </LocalizedStrict>
                        <LocalizedStrict id="product-create-deadline-panel" attrs={{ title: true, description: true }}>
                            <SalesRuleRow
                                title="Deadline"
                                description="How long before delivery orders must be placed"
                                checked={checkedDeadline}
                                onCheckChange={handleChangeDeadline}
                            >
                                <DeadlineField />
                            </SalesRuleRow>
                        </LocalizedStrict>
                    </StandardSidePanel>
                </LocalizedStrict>

                {/* hide for now as we don't handle it in mobile and pos */}
                {/* <LocalizedStrict id="product-create-order-limits-panel" attrs={{ title: true }}>
                    <StandardSidePanel
                        title="Order limits"
                        variant="switchable"
                        checked={checkedItemLimits}
                        onCheckChange={handleChangeItemLimits}
                        offContent="Order limits not activated"
                    >
                        <ItemLimitsField />
                    </StandardSidePanel>
                </LocalizedStrict> */}
            </StandardForm>
        </>
    )
}
